import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { initializeApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { environment } from '../../../environments/environment';
import { ANONYMOUS_SESSION, Session } from '../models/session.model';
import { AuthLoginResponse } from '../models/auth.model';
import { UserService } from './user.service';

const SESSION_KEY = 'riskyid_session';

const BACKOFFICE_ROLES = ['ADMINBACKOFFICE', '2'];

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly http = inject(HttpClient);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly app = initializeApp(environment.firebase);
  private readonly auth = getAuth(this.app);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly session = signal<Session>(this.loadSession());
  readonly isAuthenticated = computed(() => {
    const token = this.session().token;
    return !!token && !this.isTokenExpired(token);
  });
  readonly roles = computed(() => this.session().roles);
  readonly hasAccess = computed(() => {
    return this.isAuthenticated() && this.roles().some(r => BACKOFFICE_ROLES.includes(r.toUpperCase()));
  });

  constructor() {
    onAuthStateChanged(this.auth, async (user) => {
      if (!user) {
        this.session.set(ANONYMOUS_SESSION);
        this.clearPersistedSession();
      }
      this.loading.set(false);
    });

    window.addEventListener('storage', (event) => {
      if (event.key === SESSION_KEY || event.key === null) {
        this.checkSession();
      }
    });

    setInterval(() => {
      this.checkSession();
    }, 1000);
  }

  async login(email: string, password: string): Promise<{ ok: boolean; error?: string }> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      const uid = credential.user.uid;

      const loginResponse = await firstValueFrom(
        this.http.post<AuthLoginResponse>(`${environment.apiUrl}/api/Authentication/login`, { googleId: uid })
      );

      const rawRole = loginResponse.user.rol;
      const normalizedRole = rawRole?.toUpperCase() ?? '';

      if (normalizedRole === 'ADMINBACKOFFICE' || normalizedRole === '2') {
        const session: Session = {
        uid,
        email: loginResponse.user.email,
        token: loginResponse.token,
        refreshToken: loginResponse.refreshToken,
        userId: loginResponse.user.id,
        accountId: loginResponse.account.id,
        ownerId: loginResponse.account.ownerId,
        planId: loginResponse.account.planId,
        roles: [normalizedRole],
      };
      this.session.set(session);
      this.persistSession(session);

      this.refreshUserProfile();

      return { ok: true };
      }

      if (normalizedRole === 'ADMINISTRATOR' || normalizedRole === '0' || normalizedRole === 'USER' || normalizedRole === '1') {
        await signOut(this.auth);
        this.error.set('ACCESS_DENIED');
        this.loading.set(false);
        return { ok: false, error: 'ACCESS_DENIED' };
      }

      await signOut(this.auth);
      this.error.set('INVALID_CREDENTIALS');
      this.loading.set(false);
      return { ok: false, error: 'INVALID_CREDENTIALS' };
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      const backendMessage = this.extractBackendErrorMessage(err);
      const msg = this.mapFirebaseError(code, backendMessage);
      this.error.set(msg);
      this.loading.set(false);
      return { ok: false, error: msg };
    }
  }

  async createFirebaseUser(email: string, password: string): Promise<string> {
    const credential = await createUserWithEmailAndPassword(this.auth, email, password);
    return credential.user.uid;
  }

  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const methods = await fetchSignInMethodsForEmail(this.auth, email);
      return methods.length > 0;
    } catch {
      return false;
    }
  }

  async logout(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    await signOut(this.auth);
  }

  checkSession(): void {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<Session>;
        if (parsed.token && !this.isTokenExpired(parsed.token)) {
          const current = this.session();
          if (JSON.stringify(current) !== raw) {
            this.session.set(parsed as Session);
          }
          return;
        }
      }
    } catch { /* ignore */ }
    
    if (this.session().token) {
      this.forceLogout();
    }
  }

  forceLogout(): void {
    this.session.set(ANONYMOUS_SESSION);
    this.clearPersistedSession();
    signOut(this.auth).catch(() => {});
    this.router.navigate(['/login']);
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  async refreshUserProfile(): Promise<void> {
    const currentSession = this.session();
    const userId = currentSession.userId;
    if (!userId) return;

    try {
      const user = await firstValueFrom(this.userService.getUserByUserId(userId));
      if (!user || user.rol === undefined) return;

      let backendRol = user.rol;
      if (typeof backendRol === 'number') {
        if (backendRol === 0) backendRol = 'ADMINISTRATOR';
        else if (backendRol === 2) backendRol = 'ADMINBACKOFFICE';
        else backendRol = 'USER';
      }

      const normalized = `${backendRol}`.toUpperCase();

      if (currentSession.roles[0]?.toUpperCase() === normalized) return;

      if (BACKOFFICE_ROLES.includes(normalized)) {
        const updated: Session = { ...currentSession, roles: [normalized] };
        this.session.set(updated);
        this.persistSession(updated);
      } else {
        await this.logout();
      }
    } catch {
      // Silently fail — will retry on next navigation
    }
  }

  private mapFirebaseError(code: string, backendMessage = ''): string {
    if (backendMessage === 'INVALID_PASSWORD') {
      return 'INVALID_PASSWORD';
    }

    switch (code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
      case 'auth/invalid-login-credentials':
      case 'auth/invalid-email':
        return 'INVALID_CREDENTIALS';
      case 'auth/operation-not-allowed':
        return 'AUTH_PROVIDER_DISABLED';
      case 'auth/network-request-failed':
        return 'NETWORK_ERROR';
      case 'auth/user-disabled':
        return 'USER_DISABLED';
      case 'auth/too-many-requests':
        return 'TOO_MANY_ATTEMPTS';
      default:
        return 'UNKNOWN_ERROR';
    }
  }

  private extractBackendErrorMessage(err: unknown): string {
    const fromTokenResponse = (err as {
      customData?: { _tokenResponse?: { error?: { message?: string } } };
    })?.customData?._tokenResponse?.error?.message;

    if (typeof fromTokenResponse === 'string' && fromTokenResponse.trim().length > 0) {
      return fromTokenResponse.trim();
    }

    const fromMessage = (err as { message?: string })?.message;
    if (typeof fromMessage !== 'string') {
      return '';
    }

    const directMatch = fromMessage.match(/INVALID_[A-Z_]+/);
    if (directMatch?.[0]) {
      return directMatch[0];
    }

    return '';
  }

  private loadSession(): Session {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<Session>;
        if (parsed.token && parsed.uid && !this.isTokenExpired(parsed.token)) {
          return parsed as Session;
        }
      }
    } catch { /* ignore */ }
    return ANONYMOUS_SESSION;
  }

  private persistSession(session: Session): void {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch { /* ignore */ }
  }

  private clearPersistedSession(): void {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch { /* ignore */ }
  }
}
